import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Smartphone, X, Check } from "lucide-react";

export default function InstallAppButton({ variant = "primary", className = "", children }) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    // Already running as installed PWA → hide
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }

    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => setInstalled(true);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const handleClick = async () => {
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setPrompt(null);
    } else if (isIos) {
      setShowIos(true);
    } else {
      // Fallback: open /discreto page which has install CTA and instructions
      window.location.href = "/discreto";
    }
  };

  const baseCls = variant === "primary" ? "btn-primary" : "btn-secondary";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`${baseCls} inline-flex items-center gap-2 ${className}`}
        data-testid="install-app-btn"
      >
        <Download size={14} strokeWidth={1.5} />
        {children || t("home.cta_install")}
      </button>

      {showIos && (
        <div
          className="fixed inset-0 z-[9998] flex items-end md:items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
          onClick={() => setShowIos(false)}
          data-testid="ios-install-modal"
        >
          <div
            className="w-full max-w-md p-6 relative"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--brand-primary)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIos(false)}
              className="absolute top-3 right-3 p-1"
              style={{ color: "var(--text-muted)" }}
              aria-label="Cerrar"
              data-testid="ios-install-close"
            >
              <X size={18} />
            </button>
            <Smartphone size={28} strokeWidth={1.3} color="var(--brand-primary)" className="mb-3" />
            <div className="overline mb-2" style={{ color: "var(--brand-primary)" }}>
              {t("home.ios_install_overline")}
            </div>
            <h3 className="font-serif text-2xl mb-4" style={{ color: "var(--text-primary)" }}>
              {t("home.ios_install_title")}
            </h3>
            <ol className="text-sm space-y-3" style={{ color: "var(--text-secondary)" }}>
              <li className="flex gap-3">
                <span className="shrink-0 font-serif" style={{ color: "var(--brand-primary)" }}>1.</span>
                <span>{t("home.ios_step1_pre")} <strong style={{ color: "var(--text-primary)" }}>{t("home.ios_step1_share")}</strong> <span style={{ color: "var(--brand-primary)" }}>⎙</span> {t("home.ios_step1_post")}</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 font-serif" style={{ color: "var(--brand-primary)" }}>2.</span>
                <span>{t("home.ios_step2_pre")} <strong style={{ color: "var(--text-primary)" }}>{t("home.ios_step2_action")}</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 font-serif" style={{ color: "var(--brand-primary)" }}>3.</span>
                <span>{t("home.ios_step3_pre")} <strong style={{ color: "var(--text-primary)" }}>{t("home.ios_step3_action")}</strong>{t("home.ios_step3_post")}</span>
              </li>
            </ol>
            <div
              className="mt-5 p-3 text-xs"
              style={{ background: "rgba(212,175,55,0.1)", border: "1px solid var(--vip-gold)", color: "var(--text-secondary)" }}
            >
              <Check size={12} className="inline mr-1" style={{ color: "var(--vip-gold)" }} />
              {t("home.ios_stealth_hint_pre")} <strong>{t("home.ios_stealth_hint_link")}</strong>{t("home.ios_stealth_hint_post")} <a href="/discreto" style={{ color: "var(--brand-primary)" }}>{t("home.ios_stealth_hint_link2")}</a> {t("home.ios_stealth_hint_post2")}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
